import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector, type RootState } from "@app/store";
import { selectAccessToken } from "@app/store/auth.slice";
import { makeGql } from "@app/utils/gql";
import type { User, UsersSummary, UsersPageInfo, UserStatus, Approver } from "@app/types/users";

const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT ?? "";
const APPSYNC_API_KEY = import.meta.env.VITE_GRAPHQL_API_KEY ?? "";

const PAGE_LIMIT = 10;

function useApiHeaders() {
    return useCallback((): Record<string, string> => {
        const h: Record<string, string> = {};
        if (APPSYNC_API_KEY) h["x-api-key"] = APPSYNC_API_KEY;
        return h;
    }, []);
}

const USERS_QUERY = `
  query UsersWithAllFields($limit: Int, $cursor: String, $name: String, $email: String, $status: String, $isExpenseUser: Boolean, $sortBy: String, $sortOrder: String) {
    users(limit: $limit, cursor: $cursor, name: $name, email: $email, status: $status, isExpenseUser: $isExpenseUser, sortBy: $sortBy, sortOrder: $sortOrder) {
      summary {
        totalCount
        authorizedUsers
        activeUsers
      }
      pageInfo {
        totalCount
        cursor
        limit
        hasNextPage
      }
      users {
        userId
        legalName {
          firstName
          middleName
          lastName
          suffix
        }
        preferredName {
          firstName
          middleName
          lastName
          suffix
        }
        emailId
        status
        isTraveler
        isExpenseUser
        companyId
        managerRoleId
        legacyUserKey
        expenseProfileId
        createdAt
        updatedAt
        group {
          groupId
          groupName
          description
          isActive
          isDefault
        }
        managerUserId
        approvers {
          userId
          legalName {
            firstName
            middleName
            lastName
            suffix
          }
          preferredName {
            firstName
            middleName
            lastName
            suffix
          }
          emailId
          status
        }
        creditCards {
          creditCardKey
          description
          last4
          isDuplicate
          hasNoCharges
        }
        updatedBy {
          userId
          firstName
          lastName
          updatedAt
        }
        lastActivity
      }
    }
  }
`;

export interface UsersQueryFilters {
    name?: string;
    email?: string;
    status?: string;
    isExpenseUser?: boolean;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
}

export function useUsers(filters: UsersQueryFilters = {}) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [users, setUsers] = useState<User[]>([]);
    const [summary, setSummary] = useState<UsersSummary | null>(null);
    const [pageInfo, setPageInfo] = useState<UsersPageInfo | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cursorHistoryRef = useRef<(string | null)[]>([null]);
    const inflightRef = useRef<Set<string>>(new Set());
    const currentRequestKeyRef = useRef<string | null>(null);
    const filtersRef = useRef(filters);
    filtersRef.current = filters;
    const filterKey = `${filters.name ?? ""}|${filters.email ?? ""}|${filters.status ?? ""}|${filters.isExpenseUser ?? ""}|${filters.sortBy ?? ""}|${filters.sortOrder ?? ""}`;
    const prevFilterKeyRef = useRef(filterKey);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const loadUsers = useCallback(
        async (page: number) => {
            const cursor = cursorHistoryRef.current[page] ?? undefined;
            const key = `page-${page}-cursor-${cursor ?? "null"}-${JSON.stringify(filtersRef.current)}`;

            if (inflightRef.current.has(key)) return;
            inflightRef.current.add(key);
            currentRequestKeyRef.current = key;

            setIsLoading(true);
            setError(null);

            try {
                const f = filtersRef.current;
                const data = await gql<{ users: { summary: UsersSummary; pageInfo: UsersPageInfo; users: User[] } }>(USERS_QUERY, {
                    limit: PAGE_LIMIT,
                    cursor,
                    ...(f.name ? { name: f.name } : {}),
                    ...(f.email ? { email: f.email } : {}),
                    ...(f.status ? { status: f.status } : {}),
                    ...(typeof f.isExpenseUser === "boolean" ? { isExpenseUser: f.isExpenseUser } : {}),
                    ...(f.sortBy ? { sortBy: f.sortBy } : {}),
                    ...(f.sortOrder ? { sortOrder: f.sortOrder } : {}),
                });

                // Discard if a newer request has already taken over
                if (currentRequestKeyRef.current !== key) return;

                setUsers(data.users.users);
                setSummary(data.users.summary);
                setPageInfo(data.users.pageInfo);

                if (data.users.pageInfo.cursor && !cursorHistoryRef.current[page + 1]) {
                    cursorHistoryRef.current[page + 1] = data.users.pageInfo.cursor;
                }
            } catch (err) {
                if (currentRequestKeyRef.current !== key) return;
                setError(err instanceof Error ? err.message : "Failed to load users");
            } finally {
                inflightRef.current.delete(key);
                if (currentRequestKeyRef.current === key) {
                    setIsLoading(false);
                }
            }
        },
        [gql],
    );

    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        if (prevFilterKeyRef.current === filterKey) return;
        prevFilterKeyRef.current = filterKey;
        cursorHistoryRef.current = [null];
        inflightRef.current.clear();
        setCurrentPage(0);
        setRefreshToken((t) => t + 1);
    }, [filterKey]);

    useEffect(() => {
        void loadUsers(currentPage);
    }, [loadUsers, currentPage, refreshToken]);

    const setPage = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const refresh = useCallback(() => {
        cursorHistoryRef.current = [null];
        setCurrentPage(0);
        setRefreshToken((t) => t + 1);
    }, []);

    return {
        users,
        summary,
        pageInfo,
        currentPage,
        isLoading,
        error,
        setPage,
        refresh,
    };
}

const GET_USER_BY_ID_QUERY = `
  query GetUserById($userId: ID!) {
    getUserById(userId: $userId) {
      userId
      legalName {
        firstName
        middleName
        lastName
        suffix
      }
      preferredName {
        firstName
        middleName
        lastName
        suffix
      }
      emailId
      status
      isTraveler
      isExpenseUser
      companyId
      managerRoleId
      legacyUserKey
      expenseProfileId
      createdAt
      updatedAt
      group {
        groupId
        groupName
        description
        isActive
        isDefault
      }
      managerUserId
      manager {
        userId
        legalName {
          firstName
          middleName
          lastName
          suffix
        }
        preferredName {
          firstName
          middleName
          lastName
          suffix
        }
        emailId
        status
      }
      creditCards {
        creditCardKey
        description
        last4
        isDuplicate
        hasNoCharges
      }
      updatedBy {
        userId
        firstName
        lastName
        updatedAt
      }
      lastActivity
    }
  }
`;

export function useUserDetail(userId: string | null) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inflightRef = useRef<Set<string>>(new Set());
    const currentUserIdRef = useRef<string | null>(null);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    useEffect(() => {
        if (!userId) {
            currentUserIdRef.current = null;
            setUser(null);
            setIsLoading(false);
            setError(null);
            return;
        }

        currentUserIdRef.current = userId;

        const key = `user-${userId}`;
        if (inflightRef.current.has(key)) return;
        inflightRef.current.add(key);

        setIsLoading(true);
        setError(null);
        setUser(null);

        gql<{ getUserById: User }>(GET_USER_BY_ID_QUERY, { userId })
            .then((data) => {
                if (currentUserIdRef.current === userId) setUser(data.getUserById);
            })
            .catch((err) => {
                if (currentUserIdRef.current === userId) {
                    setError(err instanceof Error ? err.message : "Failed to load user");
                }
            })
            .finally(() => {
                inflightRef.current.delete(key);
                if (currentUserIdRef.current === userId) setIsLoading(false);
            });
    }, [userId, gql]);

    return { user, isLoading, error };
}

// ─── useUserManager ───────────────────────────────────────────────────────────
// Add User drawer — after a non-Expense user is selected from the search dropdown, fetch
// just their manager so UserDetailContent's "Manager Details" section can show it (the
// search results don't include manager, unlike the full GET_USER_BY_ID_QUERY used in update mode).

const GET_USER_MANAGER_QUERY = `
  query GetUserManager($userId: ID!) {
    getUserById(userId: $userId) {
      manager {
        userId
        legalName {
          firstName
          middleName
          lastName
          suffix
        }
        preferredName {
          firstName
          middleName
          lastName
          suffix
        }
        emailId
        status
      }
    }
  }
`;

export function useUserManager(userId: string | null) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [manager, setManager] = useState<Approver | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inflightRef = useRef<Set<string>>(new Set());
    const currentUserIdRef = useRef<string | null>(null);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    useEffect(() => {
        if (!userId) {
            currentUserIdRef.current = null;
            setManager(null);
            setIsLoading(false);
            setError(null);
            return;
        }

        currentUserIdRef.current = userId;

        const key = `user-manager-${userId}`;
        if (inflightRef.current.has(key)) return;
        inflightRef.current.add(key);

        setIsLoading(true);
        setError(null);
        setManager(null);

        gql<{ getUserById: { manager: Approver | null } }>(GET_USER_MANAGER_QUERY, { userId })
            .then((data) => {
                if (currentUserIdRef.current === userId) setManager(data.getUserById.manager);
            })
            .catch((err) => {
                if (currentUserIdRef.current === userId) {
                    setError(err instanceof Error ? err.message : "Failed to load manager");
                }
            })
            .finally(() => {
                inflightRef.current.delete(key);
                if (currentUserIdRef.current === userId) setIsLoading(false);
            });
    }, [userId, gql]);

    return { manager, isLoading, error };
}

// ─── useUpdateUser ────────────────────────────────────────────────────────────

interface UpdateUserInput {
    companyId: string;
    groupId: string;
    status?: UserStatus;
    isExpenseUser?: boolean;
}

interface UpdateUserResult {
    userId: string;
    companyId: string;
    groupId: string;
    status: UserStatus;
    success: boolean;
    message: string | null;
}

const UPDATE_USER_MUTATION = `
  mutation UpdateUser($userId: ID!, $input: UpdateUserInput!) {
    updateUser(userId: $userId, input: $input) {
      userId
      companyId
      groupId
      status
      success
      message
    }
  }
`;

export function useUpdateUser() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const updateUser = useCallback(
        async (userId: string, input: UpdateUserInput): Promise<void> => {
            if (isMountedRef.current) {
                setIsLoading(true);
                setError(null);
            }
            try {
                await gql<{ updateUser: UpdateUserResult }>(UPDATE_USER_MUTATION, { userId, input });
            } catch (err) {
                if (isMountedRef.current) {
                    const message = err instanceof Error ? err.message : "Failed to update user";
                    setError(message);
                }
                throw err;
            } finally {
                if (isMountedRef.current) setIsLoading(false);
            }
        },
        [gql],
    );

    const clearError = useCallback(() => setError(null), []);

    return { updateUser, isLoading, error, clearError };
}

// ─── useNonExpenseUsersSearch ─────────────────────────────────────────────────
// "Add User" drawer search — finds existing company users who are NOT yet Expense
// users (isExpenseUser: false), so an admin can convert one into an Expense user.
// Unlike useGroupUsersSearch, this preloads results as soon as it's enabled (no
// minimum-character gate), since the drawer should show candidates right away.

const NON_EXPENSE_USERS_QUERY = `
  query NonExpenseUsers($limit: Int, $name: String, $isExpenseUser: Boolean) {
    users(limit: $limit, name: $name, isExpenseUser: $isExpenseUser) {
      users {
        userId
        companyId
        legalName { firstName lastName }
        preferredName { firstName lastName }
        emailId
        status
      }
    }
  }
`;

export interface NonExpenseUserSearchResult {
    userId: string;
    companyId: string;
    legalName: { firstName: string | null; lastName: string | null } | null;
    preferredName: { firstName: string | null; lastName: string | null } | null;
    emailId: string | null;
    status: string | null;
}

type NonExpenseUsersQueryResult = {
    users: { users: NonExpenseUserSearchResult[] };
};

export function useNonExpenseUsersSearch(nameFilter: string, enabled: boolean = true, limit: number = 25) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [users, setUsers] = useState<NonExpenseUserSearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const requestIdRef = useRef(0);

    const headers = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, headers, GRAPHQL_ENDPOINT), [dispatch, store, headers]);

    const trimmedFilter = nameFilter.trim();

    useEffect(() => {
        if (!enabled) {
            requestIdRef.current++; // invalidate any in-flight request so its late response can't repopulate state
            setUsers([]);
            setIsLoading(false);
            setError(null);
            return;
        }
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);

        gql<NonExpenseUsersQueryResult>(NON_EXPENSE_USERS_QUERY, {
            limit,
            isExpenseUser: false,
            ...(trimmedFilter ? { name: trimmedFilter } : {}),
        })
            .then((data) => {
                if (requestIdRef.current !== requestId) return;
                setUsers(data.users.users);
            })
            .catch((err) => {
                if (requestIdRef.current !== requestId) return;
                setError(err instanceof Error ? err.message : "Failed to search users");
            })
            .finally(() => {
                if (requestIdRef.current === requestId) setIsLoading(false);
            });
    }, [gql, trimmedFilter, enabled, limit]);

    return { users, isLoading, error };
}
